import React, { useEffect, useRef } from 'react';
import { AlertTriangle, Image, FileText, Clock, User, X } from 'lucide-react';
import { CancellationReasonTypes } from '../../interfaces/exams.interface';



interface ExamCancellationNotificationProps {
  cancellationDetails?: CancellationReasonTypes;
  onClose?: () => void;
}

const ExamCancellationNotification: React.FC<ExamCancellationNotificationProps> = ({
  cancellationDetails,
  onClose
}) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

   useEffect(() => {
    if(cancellationDetails){
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node) && onClose) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }
  }, [onClose]);

  if(!cancellationDetails) return<></>

  return (
    <div className="min-h-screen bg-[rgba(0,0,0,0.5)] flex items-center justify-center p-4 z-[9999] fixed w-screen ">
      <div className="bg-white rounded-lg shadow-lg  w-[80%] " ref={modalRef}>
        {/* Header */}
        <div className="bg-red-500 text-white p-6 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-red-700 rounded-lg">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Exam Cancelled</h1>
                <p className="text-red-100 text-sm">This candidate's exam has been cancelled by the administrator</p>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 hover:bg-red-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[75vh] overflow-auto">
          {/* Exam Details */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h2 className="text-xl text-gray-900 mb-3  ">Exam Details</h2>
            <div className=" gap-4 text-sm flex flex-col gap-y-4">
              <div className="flex items-center space-x-2">
                <FileText className="w-4 h-4 text-gray-500" />
                <span className="text-gray-600">Exam:</span>
                <span className="font-medium">{cancellationDetails.exam_group_name } ({cancellationDetails.exam_paper_name})</span>
              </div>
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4 text-gray-500" />
                <span className="text-gray-600">Candidate:</span>
                <span className="font-medium">{cancellationDetails.candidate_name}</span>
                
              </div>
              {/* <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-gray-500" />
                <span className="text-gray-600">Cancelled At:</span>
                <span className="font-medium">{formatDate(cancellationDetails.cancelled_at)}</span>
              </div>
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4 text-gray-500" />
                <span className="text-gray-600">Cancelled By:</span>
                <span className="font-medium capitalize">{cancellationDetails.cancelled_by}</span>
              </div> */}
            </div>
          </div>

          {/* Reason */}
          <div className="mb-6">
            <h2 className="font-medium text-gray-900 mb-3">Reason for Cancellation</h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800">{cancellationDetails.reason}</p>
            </div>
          </div>

          {/* Evidence */}
          {cancellationDetails.images && cancellationDetails.images.length > 0 && (
            <div className="mb-6">
              <h2 className="font-medium text-gray-900 mb-3 flex items-center">
                <Image className="w-5 h-5 mr-2" />
                Evidence ({cancellationDetails.images.length} images)
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {cancellationDetails.images.map((imageUrl, index) => (
                  <div key={index} className="aspect-square bg-gray-100 rounded-lg overflow-hidden border">
                    <img
                      src={
                        `data:image/jpeg;base64,${imageUrl.base64}` ||
                        ""
                      }
                      alt={`Evidence ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                These images were flagged as violations of the exam guidelines.
              </p>
            </div>
          )}

          {/* Important Information */}
           <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-blue-900 mb-2">Important Information</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Your exam attempt has been invalidated</li>
              <li>• This decision is final and cannot be appealed</li>
              <li>• Please contact the exam administrator if you have questions</li>
              <li>• Future exam attempts may be restricted</li>
            </ul>
          </div>

     
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Need Help?</h3>
            <p className="text-sm text-gray-600 mb-2">
              If you believe this cancellation was made in error or have questions about this decision, 
              please contact the exam administration team.
            </p>
            <div className="flex space-x-4 text-sm">
              <a 
                href="mailto:cbt@msqube.com" 
                className="text-blue-600 hover:text-blue-800"
              >
                cbt@msqube.com
              </a>
              <span className="text-gray-300">|</span>
              <a 
                href="tel:033-4814-4250" 
                className="text-blue-600 hover:text-blue-800"
              >
                033-4814-4250
              </a>
            </div>
          </div> 
        </div>
        <div className="my-2 py-2 flex justify-center">
          <button className='px-8 py-1 rounded-sm uppercase bg-blue-500 hover:bg-blue-700 text-white ' onClick={onClose}>
            Close
          </button>
        </div>

        {/* Footer */}
        {/* <div className="bg-gray-50 p-4 rounded-b-lg border-t">
          <p className="text-center text-sm text-gray-600">
            Exam ID: {cancellationDetails.exam_id} | Generated on {formatDate(new Date().toISOString())}
          </p>
        </div> */}
      </div>
    </div>
  );
};

export default ExamCancellationNotification;